import { useEffect, useRef } from "react";

/**
 * THE READ→EDIT FOCUS POLICY — "the Edit button hands the keyboard over".
 *
 * ── THE DEFECT THIS EXISTS TO CLOSE (owner-measured, live, 2026-07-30) ────────
 * On https://e8.cerebr8.link/w/wettformat/article/25: open the article (read
 * mode, 6 canvases), press **Edit**. Edit mode mounts CORRECTLY — the hidden
 * `<textarea aria-label="Document editor">` input sink exists and the editor
 * handle is registered. And then:
 *
 *     document.activeElement === BODY
 *     Backspace  → document byte-identical (35181 bytes both sides)
 *     "a"        → document byte-identical
 *
 * Not a Backspace bug. **Every keystroke** went to `<body>` until the reviewer
 * happened to click on the page. After a real `pointerdown` the sink took focus
 * and Backspace deleted a character. So the engine, the sink, the intent routing
 * and the docz bridge were all fine; entering edit mode simply never moved
 * keyboard focus. An editor you must click before it listens is not an editor.
 *
 * It survived four days of green checks because every automated check called
 * `editor.focus()` itself, or dispatched straight into the sink. Neither
 * exercises the mount path a human takes. Hence the rule for anything asserting
 * this module: **the test may not call `.focus()`.**
 *
 * ── WHY A POLICY MODULE AND NOT A ONE-LINE EFFECT ────────────────────────────
 * "Focus the editor" is trivial. "Focus the editor and never at any other time"
 * is the actual requirement, and the naive `useEffect(() => handle?.focus())`
 * form is WORSE than the bug: it re-fires on every render — every keystroke tick,
 * every relayout, every toolbar revision bump — and yanks the caret out of the
 * ribbon, the comment composer and any open dialog. So the policy is stated once,
 * here, with its guards, instead of being re-guessed at each call site.
 *
 * THE GUARDS, each one a failure this would otherwise ship:
 *
 *  1. **EDGE, not level.** Arming happens only when `editable` goes false→true
 *     (a mount already in edit mode counts — `prev` starts false). Leaving edit
 *     disarms. A re-render with `editable` unchanged does not re-arm, so the
 *     effect below cannot fire twice for one transition.
 *  2. **ONE SHOT.** The arm is spent the moment it is honoured (or declined), so
 *     an editor RE-mount inside edit mode — a save, a version return, a DPR
 *     change — takes no focus. The reviewer keeps whatever they were typing in.
 *  3. **NEVER STEAL FROM A TEXT ENTRY.** If the keyboard is currently in some
 *     other input — the comment composer, a rename dialog, the search box — the
 *     arm is DROPPED, not deferred. A button (the mode toggle itself, the ribbon)
 *     is fair game: that is where focus legitimately is at the moment of the
 *     transition, and leaving it there is the whole bug.
 *  4. **NO TRAP ON THE WAY BACK.** Read mode is not touched at all. The editor
 *     subtree unmounts and the browser's own "focused element left the document"
 *     handling returns focus to `<body>` — there is nothing to release and
 *     nothing to hold.
 *
 * The CARET is not this module's business and does not need to be: the upstream
 * `EditorCore` constructor seeds `selection = collapsed(docStart(...))`
 * (`web-page-layout/src/editor-core.ts`), so a surface that has never been
 * clicked already holds a collapsed caret at the start of the document. Focus is
 * the only missing half — which is exactly why Backspace worked immediately
 * after a click and never before one.
 */

/** The minimum of `ElaborateEditorHandle` this policy needs: something that can
 *  be asked to take the keyboard. `focus` is optional on the shared handle
 *  contract, and a handle that does not answer it is honestly un-focusable. */
export interface FocusableEditorHandle {
  readonly focus?: (() => void) | undefined;
}

/**
 * Is the keyboard currently inside a TEXT ENTRY that is not ours — somewhere a
 * human could be mid-sentence?
 *
 * Buttons, links and the document body are NOT text entries: focus sitting on
 * the Edit button is precisely the state the transition must move out of. An
 * `<input type="checkbox">` is a button in this sense too — nothing is being
 * typed into it.
 *
 * `ownRoot` is the editor's own subtree; focus already inside it (the sink from
 * a previous transition) is ours to re-take and never counts as foreign.
 */
export function isForeignTextEntry(
  active: Element | null | undefined,
  ownRoot: Element | null | undefined,
): boolean {
  if (!active) return false;
  if (ownRoot?.contains(active)) return false;
  const el = active as HTMLElement;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  if (tag === "TEXTAREA") return true;
  if (tag === "INPUT") {
    // Only the flavours that accept typed characters. `type` normalises to
    // "text" when the attribute is absent or unrecognised.
    const type = (el as HTMLInputElement).type;
    return (
      type !== "button" &&
      type !== "submit" &&
      type !== "reset" &&
      type !== "checkbox" &&
      type !== "radio" &&
      type !== "range" &&
      type !== "file" &&
      type !== "image" &&
      type !== "color"
    );
  }
  return false;
}

export interface EditModeFocusOptions {
  /** Is the surface in EDIT mode right now? The false→true edge is the trigger. */
  readonly editable: boolean;
  /** The live editor handle, or `null` while the editor has not published one. */
  readonly handle: FocusableEditorHandle | null;
  /** The editor's own DOM subtree — used only to tell OUR sink from a foreign
   *  text entry (guard 3). Omitted/`null` is safe: it just means "no subtree of
   *  ours is focused", which is true before the editor mounts. */
  readonly ownRootRef?: { readonly current: Element | null };
}

/**
 * Hand the keyboard to the editor on the read→edit transition — once, and only
 * then. See the module header for the guards and the defect.
 *
 * Ordering note: the ARM effect is declared before the FIRE effect on purpose.
 * React runs a component's effects in declaration order within one commit, so on
 * the commit where `editable` flips true the arm is already set when the fire
 * effect runs — even in the case where the handle happens to be present on that
 * same commit.
 */
export function useEditModeFocus(opts: EditModeFocusOptions): void {
  const { editable, handle, ownRootRef } = opts;
  const armed = useRef(false);

  // GUARD 1 — the EDGE. Deps are `[editable]` alone, so this runs on mount and on
  // each genuine mode change, never on an unrelated re-render.
  useEffect(() => {
    armed.current = editable;
  }, [editable]);

  // GUARDS 2 + 3 — spend the arm at most once, and never over someone's typing.
  useEffect(() => {
    if (!editable || !armed.current) return;
    const take = handle?.focus;
    if (!take) return; // no handle yet — stay armed for the commit that has one
    armed.current = false; // spent either way: declined is still answered
    if (isForeignTextEntry(document.activeElement, ownRootRef?.current ?? null)) {
      return;
    }
    take();
  }, [editable, handle, ownRootRef]);
}
